import React from 'react';
import { motion } from 'framer-motion';
import { Settings2, Eye, EyeOff, RotateCcw, X } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { DASHBOARD_SECTIONS } from '../hooks/useDashboardPreferences';

const DashboardCustomizer = ({ 
  preferences, 
  toggleSection, 
  resetToDefaults,
  isSectionVisible 
}) => {
  const [open, setOpen] = React.useState(false);

  const visibleCount = Object.values(DASHBOARD_SECTIONS).filter(
    s => isSectionVisible(s.id)
  ).length;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className="gap-2 text-xs"
          data-testid="customize-dashboard-btn"
        >
          <Settings2 className="w-4 h-4" />
          <span className="hidden sm:inline">Personnaliser</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Settings2 className="w-5 h-5 text-primary" />
            Personnaliser le tableau de bord
          </DialogTitle>
          <DialogDescription>
            Choisissez les sections à afficher sur votre page d'accueil
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[400px] pr-4">
          <div className="space-y-1">
            {Object.values(DASHBOARD_SECTIONS).map((section) => {
              const isVisible = isSectionVisible(section.id);
              const canHide = section.canHide !== false;

              return (
                <motion.div
                  key={section.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                    isVisible ? 'bg-secondary/30' : 'bg-secondary/10 opacity-60'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-lg">{section.icon}</span>
                    <Label 
                      htmlFor={`section-${section.id}`} 
                      className={`cursor-pointer ${!isVisible ? 'text-muted-foreground' : ''}`}
                    >
                      {section.label}
                    </Label>
                  </div>
                  
                  {canHide ? (
                    <div className="flex items-center gap-2">
                      {isVisible ? (
                        <Eye className="w-4 h-4 text-green-500" />
                      ) : (
                        <EyeOff className="w-4 h-4 text-muted-foreground" />
                      )}
                      <Switch
                        id={`section-${section.id}`}
                        data-testid={`customize-toggle-${section.id}`}
                        checked={isVisible}
                        onCheckedChange={() => toggleSection(section.id)}
                      />
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground px-2 py-1 bg-secondary/50 rounded">
                      Obligatoire
                    </span>
                  )}
                </motion.div>
              );
            })}
          </div>
        </ScrollArea>

        <div className="flex items-center justify-between pt-4 border-t border-border">
          <span className="text-xs text-muted-foreground">
            {visibleCount}/{Object.keys(DASHBOARD_SECTIONS).length} sections affichées
          </span>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={resetToDefaults}
              className="gap-1"
            >
              <RotateCcw className="w-3 h-3" />
              Réinitialiser
            </Button>
            <Button 
              size="sm" 
              onClick={() => setOpen(false)}
              className="bg-primary text-primary-foreground"
            >
              Terminé
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DashboardCustomizer;
